/**
 * Widget Registry
 *
 * Central registry for all available dashboard widgets.
 * This file defines widget types, their default configurations,
 * and provides methods to access widget metadata.
 */

// Widget type constants
export const WIDGET_TYPES = {
  KPI_CARD: 'kpi_card',
  REVENUE_CHART: 'revenue_chart',
  CLIENT_CHART: 'client_chart',
  RECENT_ACTIVITY: 'recent_activity',
  QUICK_ACTIONS: 'quick_actions',
  CALENDAR_PREVIEW: 'calendar_preview',
  FINANCIAL_SUMMARY: 'financial_summary',
  CLIENT_PIPELINE: 'client_pipeline',
  EXPENSE_CHART: 'expense_chart',
  PROFIT_LOSS: 'profit_loss',
  TASKS_REMINDERS: 'tasks_reminders',
  STACKABLE_TILES: 'stackable_tiles',
  FORMULA_BUILDER: 'formula_builder',
  GROUP: 'group'
};

// Widget categories for organization
export const WIDGET_CATEGORIES = {
  METRICS: 'Metrics & KPIs',
  CHARTS: 'Charts & Analytics',
  ACTIVITIES: 'Activities & Events',
  TOOLS: 'Quick Tools',
  FINANCIAL: 'Financial'
};

/**
 * Widget metadata and default configurations
 */
export const WIDGET_DEFINITIONS = {
  [WIDGET_TYPES.KPI_CARD]: {
    id: WIDGET_TYPES.KPI_CARD,
    name: 'KPI Card',
    description: 'Display a key performance indicator with trend',
    category: WIDGET_CATEGORIES.METRICS,
    icon: '📊',
    defaultSize: { w: 3, h: 2, minW: 2, minH: 2 },
    configurable: true,
    defaultConfig: {
      metric: 'revenue',
      label: 'Total Revenue',
      format: 'currency',
      showTrend: true,
      timeframe: '30d'
    }
  },

  [WIDGET_TYPES.REVENUE_CHART]: {
    id: WIDGET_TYPES.REVENUE_CHART,
    name: 'Revenue Chart',
    description: 'Line or bar chart showing revenue over time',
    category: WIDGET_CATEGORIES.CHARTS,
    icon: '📈',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 },
    configurable: true,
    defaultConfig: {
      chartType: 'line',
      timeframe: '30d',
      showComparison: false
    }
  },

  [WIDGET_TYPES.CLIENT_CHART]: {
    id: WIDGET_TYPES.CLIENT_CHART,
    name: 'Client Analytics',
    description: 'Chart showing client growth and statistics',
    category: WIDGET_CATEGORIES.CHARTS,
    icon: '👥',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 },
    configurable: true,
    defaultConfig: {
      chartType: 'bar',
      metric: 'new_clients',
      timeframe: '30d'
    }
  },

  [WIDGET_TYPES.RECENT_ACTIVITY]: {
    id: WIDGET_TYPES.RECENT_ACTIVITY,
    name: 'Recent Activity',
    description: 'Feed of recent business activities',
    category: WIDGET_CATEGORIES.ACTIVITIES,
    icon: '🔔',
    defaultSize: { w: 4, h: 5, minW: 3, minH: 4 },
    configurable: true,
    defaultConfig: {
      maxItems: 10,
      activityTypes: ['all'],
      showTimestamp: true
    }
  },

  [WIDGET_TYPES.QUICK_ACTIONS]: {
    id: WIDGET_TYPES.QUICK_ACTIONS,
    name: 'Quick Actions',
    description: 'Buttons for common tasks',
    category: WIDGET_CATEGORIES.TOOLS,
    icon: '⚡',
    defaultSize: { w: 3, h: 2, minW: 2, minH: 2 },
    configurable: true,
    defaultConfig: {
      actions: ['new_invoice', 'new_client', 'new_booking', 'new_expense']
    }
  },

  [WIDGET_TYPES.CALENDAR_PREVIEW]: {
    id: WIDGET_TYPES.CALENDAR_PREVIEW,
    name: 'Calendar Preview',
    description: 'Upcoming appointments and bookings',
    category: WIDGET_CATEGORIES.ACTIVITIES,
    icon: '📅',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3 },
    configurable: true,
    defaultConfig: {
      view: 'week',
      maxEvents: 5,
      showAllDay: true
    }
  },

  [WIDGET_TYPES.FINANCIAL_SUMMARY]: {
    id: WIDGET_TYPES.FINANCIAL_SUMMARY,
    name: 'Financial Summary',
    description: 'Overview of income, expenses, and profit',
    category: WIDGET_CATEGORIES.FINANCIAL,
    icon: '💰',
    defaultSize: { w: 6, h: 3, minW: 4, minH: 2 },
    configurable: true,
    defaultConfig: {
      timeframe: '30d',
      showBreakdown: true,
      currency: 'USD'
    }
  },

  [WIDGET_TYPES.CLIENT_PIPELINE]: {
    id: WIDGET_TYPES.CLIENT_PIPELINE,
    name: 'Client Pipeline',
    description: 'Funnel view of client acquisition stages',
    category: WIDGET_CATEGORIES.CHARTS,
    icon: '🎯',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 },
    configurable: true,
    defaultConfig: {
      stages: ['lead', 'prospect', 'active', 'retained'],
      showValues: true
    }
  },

  [WIDGET_TYPES.EXPENSE_CHART]: {
    id: WIDGET_TYPES.EXPENSE_CHART,
    name: 'Expense Breakdown',
    description: 'Pie or donut chart of expense categories',
    category: WIDGET_CATEGORIES.FINANCIAL,
    icon: '💳',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3 },
    configurable: true,
    defaultConfig: {
      chartType: 'donut',
      timeframe: '30d',
      showLegend: true
    }
  },

  [WIDGET_TYPES.PROFIT_LOSS]: {
    id: WIDGET_TYPES.PROFIT_LOSS,
    name: 'Profit & Loss',
    description: 'Quick P&L statement widget',
    category: WIDGET_CATEGORIES.FINANCIAL,
    icon: '📉',
    defaultSize: { w: 4, h: 3, minW: 3, minH: 2 },
    configurable: true,
    defaultConfig: {
      timeframe: '30d',
      showComparison: true,
      format: 'compact'
    }
  },

  [WIDGET_TYPES.TASKS_REMINDERS]: {
    id: WIDGET_TYPES.TASKS_REMINDERS,
    name: 'Tasks & Reminders',
    description: 'List of pending tasks and reminders',
    category: WIDGET_CATEGORIES.TOOLS,
    icon: '✅',
    defaultSize: { w: 3, h: 4, minW: 2, minH: 3 },
    configurable: true,
    defaultConfig: {
      maxItems: 8,
      showCompleted: false,
      sortBy: 'dueDate'
    }
  },

  [WIDGET_TYPES.STACKABLE_TILES]: {
    id: WIDGET_TYPES.STACKABLE_TILES,
    name: 'Stackable Metrics',
    description: 'Stack multiple KPI tiles together for compact view',
    category: WIDGET_CATEGORIES.METRICS,
    icon: '📚',
    defaultSize: { w: 6, h: 3, minW: 4, minH: 3 },
    configurable: true,
    defaultConfig: {
      tiles: []
    }
  },

  [WIDGET_TYPES.FORMULA_BUILDER]: {
    id: WIDGET_TYPES.FORMULA_BUILDER,
    name: 'Custom Formulas',
    description: 'Build custom formulas and equations for KPIs',
    category: WIDGET_CATEGORIES.TOOLS,
    icon: '🧮',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3 },
    configurable: true,
    defaultConfig: {
      formulas: []
    }
  },

  [WIDGET_TYPES.GROUP]: {
    id: WIDGET_TYPES.GROUP,
    name: 'Widget Group',
    description: 'iPhone-style folder grouping multiple widgets together',
    category: WIDGET_CATEGORIES.TOOLS,
    icon: '📁',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3 },
    configurable: true,
    defaultConfig: {
      title: 'Widget Group',
      groupedWidgets: [],
      expanded: false
    }
  }
};

/**
 * Get all widget definitions
 */
export const getAllWidgets = () => {
  return Object.values(WIDGET_DEFINITIONS);
};

// Every tile that renders money. CLIENT_CHART currently renders the revenue
// chart, FORMULA_BUILDER computes from revenue/expense/chart-of-accounts, and
// RECENT_ACTIVITY lists invoice/payment amounts.
const FINANCIAL_WIDGET_TYPES = new Set([
  WIDGET_TYPES.REVENUE_CHART,
  WIDGET_TYPES.CLIENT_CHART,
  WIDGET_TYPES.FINANCIAL_SUMMARY,
  WIDGET_TYPES.EXPENSE_CHART,
  WIDGET_TYPES.PROFIT_LOSS,
  WIDGET_TYPES.FORMULA_BUILDER,
  WIDGET_TYPES.RECENT_ACTIVITY
]);
const FINANCIAL_TEXT = /\b(revenue|income|expense|profit|loss|invoice|payment|payout|fare|rate|price|amount|financial)\b/i;

export const isFinancialWidget = (widget) => {
  if (!widget) return false;
  if (FINANCIAL_WIDGET_TYPES.has(widget.type) || widget.type === WIDGET_TYPES.STACKABLE_TILES) return true;
  if (widget.type === WIDGET_TYPES.KPI_CARD) {
    return widget.config?.format === 'currency'
      || FINANCIAL_TEXT.test(`${widget.config?.metric || ''} ${widget.config?.label || ''}`);
  }
  return false;
};

export const widgetRequiredAreas = (widget) => {
  const areas = [];
  if (isFinancialWidget(widget)) areas.push('financial');
  if ([WIDGET_TYPES.CLIENT_CHART, WIDGET_TYPES.CLIENT_PIPELINE].includes(widget?.type)) areas.push('crm');
  if (widget?.type === WIDGET_TYPES.CALENDAR_PREVIEW) areas.push('dispatch');
  if (widget?.type === WIDGET_TYPES.TASKS_REMINDERS) areas.push('tasks');
  return areas;
};

export const widgetRequiredArea = (widget) => widgetRequiredAreas(widget)[0] || null;

export const isWidgetAllowed = (widget, areaAccess = {}) =>
  widgetRequiredAreas(widget).every((area) => areaAccess[area] !== false);

export const filterDashboardWidgets = (widgets, areaAccess = {}) => {
  return (widgets || []).flatMap((widget) => {
    if (!isWidgetAllowed(widget, areaAccess)) return [];
    if (widget.type === WIDGET_TYPES.GROUP) {
      const groupedWidgets = filterDashboardWidgets(widget.config?.groupedWidgets, areaAccess);
      return groupedWidgets.length
        ? [{ ...widget, config: { ...widget.config, groupedWidgets } }]
        : [];
    }
    if (widget.type === WIDGET_TYPES.QUICK_ACTIONS) {
      const actionAreas = {
        new_invoice: 'financial',
        new_expense: 'financial',
        new_client: 'crm',
        new_booking: 'dispatch',
        view_reports: 'reports',
        settings: 'settings',
      };
      const actions = (widget.config?.actions || []).filter((action) =>
        !actionAreas[action] || areaAccess[actionAreas[action]] !== false
      );
      if (!actions.length) return [];
      return [{ ...widget, config: { ...widget.config, actions } }];
    }
    return [widget];
  });
};

/**
 * Get widgets by category
 */
export const getWidgetsByCategory = (category) => {
  return Object.values(WIDGET_DEFINITIONS).filter(
    widget => widget.category === category
  );
};

/**
 * Get widget definition by type
 */
export const getWidgetDefinition = (type) => {
  return WIDGET_DEFINITIONS[type] || null;
};

/**
 * Create a new widget instance with default configuration
 */
export const createWidgetInstance = (type, position = {}) => {
  const definition = getWidgetDefinition(type);
  if (!definition) {
    throw new Error(`Widget type "${type}" not found in registry`);
  }

  const id = `widget_${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return {
    id,
    type,
    x: position.x || 0,
    y: position.y || 0,
    w: position.w || definition.defaultSize.w,
    h: position.h || definition.defaultSize.h,
    minW: definition.defaultSize.minW,
    minH: definition.defaultSize.minH,
    config: { ...definition.defaultConfig }
  };
};

/**
 * Default dashboard templates
 */
export const DASHBOARD_TEMPLATES = {
  EXECUTIVE: {
    id: 'executive',
    name: 'Executive Dashboard',
    description: 'High-level overview for executives',
    widgets: [
      { type: WIDGET_TYPES.FINANCIAL_SUMMARY, x: 0, y: 0, w: 6, h: 3 },
      { type: WIDGET_TYPES.REVENUE_CHART, x: 6, y: 0, w: 6, h: 3 },
      { type: WIDGET_TYPES.KPI_CARD, x: 0, y: 3, w: 3, h: 2 },
      { type: WIDGET_TYPES.KPI_CARD, x: 3, y: 3, w: 3, h: 2 },
      { type: WIDGET_TYPES.CLIENT_CHART, x: 6, y: 3, w: 6, h: 4 },
      { type: WIDGET_TYPES.RECENT_ACTIVITY, x: 0, y: 5, w: 6, h: 4 }
    ]
  },

  SALES: {
    id: 'sales',
    name: 'Sales Dashboard',
    description: 'Focused on sales and client management',
    widgets: [
      { type: WIDGET_TYPES.CLIENT_PIPELINE, x: 0, y: 0, w: 8, h: 4 },
      { type: WIDGET_TYPES.QUICK_ACTIONS, x: 8, y: 0, w: 4, h: 2 },
      { type: WIDGET_TYPES.RECENT_ACTIVITY, x: 8, y: 2, w: 4, h: 5 },
      { type: WIDGET_TYPES.KPI_CARD, x: 0, y: 4, w: 4, h: 2 },
      { type: WIDGET_TYPES.KPI_CARD, x: 4, y: 4, w: 4, h: 2 },
      { type: WIDGET_TYPES.REVENUE_CHART, x: 0, y: 6, w: 8, h: 4 }
    ]
  },

  FINANCE: {
    id: 'finance',
    name: 'Finance Dashboard',
    description: 'Financial management and reporting',
    widgets: [
      { type: WIDGET_TYPES.PROFIT_LOSS, x: 0, y: 0, w: 4, h: 3 },
      { type: WIDGET_TYPES.FINANCIAL_SUMMARY, x: 4, y: 0, w: 8, h: 3 },
      { type: WIDGET_TYPES.REVENUE_CHART, x: 0, y: 3, w: 6, h: 4 },
      { type: WIDGET_TYPES.EXPENSE_CHART, x: 6, y: 3, w: 6, h: 4 },
      { type: WIDGET_TYPES.RECENT_ACTIVITY, x: 0, y: 7, w: 12, h: 3 }
    ]
  },

  OPERATIONS: {
    id: 'operations',
    name: 'Operations Dashboard',
    description: 'Daily operations and task management',
    widgets: [
      { type: WIDGET_TYPES.CALENDAR_PREVIEW, x: 0, y: 0, w: 6, h: 4 },
      { type: WIDGET_TYPES.TASKS_REMINDERS, x: 6, y: 0, w: 3, h: 4 },
      { type: WIDGET_TYPES.QUICK_ACTIONS, x: 9, y: 0, w: 3, h: 2 },
      { type: WIDGET_TYPES.RECENT_ACTIVITY, x: 9, y: 2, w: 3, h: 5 },
      { type: WIDGET_TYPES.CLIENT_CHART, x: 0, y: 4, w: 9, h: 4 }
    ]
  },

  CUSTOM: {
    id: 'custom',
    name: 'Custom Dashboard',
    description: 'Start with a blank canvas',
    widgets: []
  }
};

const widgetRegistry = {
  WIDGET_TYPES,
  WIDGET_CATEGORIES,
  WIDGET_DEFINITIONS,
  DASHBOARD_TEMPLATES,
  getAllWidgets,
  getWidgetsByCategory,
  getWidgetDefinition,
  createWidgetInstance
};

export default widgetRegistry;